<template>
    <div
        :class="classVideoWithContent"
        class="video-with-content"
    >
        <div class="video-container">
            <video
                ref="videoElement"
                :class="classVideo"
                :src="data.video.url"
                controls
                preload="none"
                class="video"
            ></video>

            <CmsImage
                v-if="!isPlaying"
                :data="data.coverImage"
                :is-eager="hasEagerImage"
                fit="cover"
            />

            <ButtonRound
                v-if="!isPlaying"
                aria-label="Play video"
                class="video-button"
                icon="angle-right"
                @click="playVideo"
            />
        </div>

        <div class="content">
            <ContentComponentItem
                v-for="(component, key) in data.content"
                :key="key"
                :background="background"
                :data="component"
                :page-type="pageType"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import type { Color, ComponentVideoWithContentFragment } from '@/graphql/api'
import type { ButtonColors } from '@/components/ui/buttons'
import { getColorForBackground } from '@/components/ui/buttons'
import { cmsColorToButtonColor } from '@/components/cms'
import type { Colors } from '@/components/ui'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const props = defineProps<{
    data: ComponentVideoWithContentFragment
    background: Color
    hasEagerImage?: boolean
    pageType: PageType
}>()

const backgroundColor = computed(
    (): ButtonColors => cmsColorToButtonColor(props.background)
)

const color = computed(
    (): Colors => getColorForBackground(backgroundColor.value)
)

const isPlaying = ref(false)

const videoElement: Ref<HTMLVideoElement | null> = ref(null)

const playVideo = () => {
    if (videoElement.value) {
        videoElement.value.play()
    }

    isPlaying.value = true
}

const classVideoWithContent = computed(() =>
    `${props.data.videoPosition}-video`.toLocaleLowerCase()
)

const classVideo = computed(() => ({
    hidden: !isPlaying.value,
}))
</script>

<style lang="scss" scoped>
.video-with-content {
    background: v-bind(backgroundColor);
    color: v-bind(color);
    width: 100%;

    @include min-width(1024) {
        display: flex;
        align-items: center;
        height: 640px;
    }

    &.right-video {
        @include min-width(1024) {
            flex-direction: row-reverse;
        }

        .video-container {
            .video-button {
                @include min-width(1024) {
                    right: auto;
                    left: -60px;
                }
            }
        }
    }

    .video-container {
        width: 100%;
        height: 300px;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
        position: relative;

        @include min-width(640) {
            height: 400px;
        }

        @include min-width(1024) {
            height: 100%;
        }

        .video-button {
            background: $white;
            color: $blue;
            width: 80px;
            height: 80px;
            position: absolute;
            bottom: -40px;
            left: 0;
            right: 0;
            margin: 0 auto;

            @include min-width(1024) {
                width: 120px;
                height: 120px;
                bottom: 50px;
                right: -60px;
                left: auto;
                margin: 0;
            }
        }

        .video {
            background: $black;
            width: 100%;
            height: 100%;

            &.hidden {
                display: none;
            }
        }
    }

    .content {
        width: 100%;
        padding: 80px 20px 40px;

        @include min-width(1024) {
            max-width: 500px;
            padding: 80px 40px 40px;
        }
    }
}
</style>
